import {
  searchClient,
  ALGOLIA_COLLECTION_PAGE_CONFIG,
  buildCollectionFilters,
} from './client';

const filtersCache = new Map();

function getCachedCollectionFilters(handle) {
  if (!filtersCache.has(handle)) {
    filtersCache.set(handle, buildCollectionFilters(handle));
  }
  return filtersCache.get(handle);
}

/**
 * Fetches products from Algolia for a given Shopify collection.
 * Returns hits, facet values, facet ordering, and pagination metadata.
 *
 * @param {Object} options
 * @param {string} options.collectionHandle - Shopify collection handle (e.g. "womens-shirts")
 * @param {number} [options.page=0] - Zero-based page number
 * @param {number} [options.hitsPerPage] - Products per page (defaults to config value)
 * @param {string} [options.sortKey='featured'] - Sort key matching sortIndices config
 * @param {Array} [options.facetFilters=[]] - Algolia facetFilters (OR within inner arrays, AND across outer)
 * @param {string} [options.query=''] - Optional search query within the collection
 * @returns {Promise<Object>}
 */
export async function fetchCollectionProducts({
  collectionHandle,
  page = 0,
  hitsPerPage = ALGOLIA_COLLECTION_PAGE_CONFIG.hitsPerPage,
  sortKey = 'featured',
  facetFilters = [],
  numericFilters = [],
  query = '',
  ruleContexts = [],
}) {
  const indexName = ALGOLIA_COLLECTION_PAGE_CONFIG.sortIndices[sortKey]
    || ALGOLIA_COLLECTION_PAGE_CONFIG.indexName;

  const filters = getCachedCollectionFilters(collectionHandle);

  const request = {
    indexName,
    query,
    filters,
    facetFilters,
    page,
    hitsPerPage,
    attributesToRetrieve: ['handle', 'objectID'],
    facets: ALGOLIA_COLLECTION_PAGE_CONFIG.facets,
    clickAnalytics: true,
    facetingAfterDistinct: false,
    maxValuesPerFacet: ALGOLIA_COLLECTION_PAGE_CONFIG.maxValuesPerFacet,
  };

  if (ruleContexts.length) {
    request.ruleContexts = ruleContexts;
  }

  if (numericFilters.length) {
    request.numericFilters = numericFilters;
  }

  const response = await searchClient.search({ requests: [request] });
  const result = response.results[0];

  return {
    hits: result.hits || [],
    nbHits: result.nbHits || 0,
    page: result.page || 0,
    nbPages: result.nbPages || 0,
    hitsPerPage: result.hitsPerPage || hitsPerPage,
    facets: result.facets || {},
    facets_stats: result.facets_stats || {},
    renderingContent: result.renderingContent || null,
    queryID: result.queryID || null,
    index: result.index || indexName,
  };
}

/**
 * Fetches all matching products for a collection in a single request.
 * Uses a high hitsPerPage to avoid multi-page batching.
 *
 * @param {Object} options - Same as fetchCollectionProducts
 * @param {number} [options.maxProducts=500] - Max products to return
 * @returns {Promise<Object>}
 */
export async function fetchAllCollectionProducts({
  collectionHandle,
  sortKey = 'featured',
  facetFilters = [],
  numericFilters = [],
  query = '',
  maxProducts = 500,
  ruleContexts = [],
}) {
  return fetchCollectionProducts({
    collectionHandle,
    page: 0,
    hitsPerPage: maxProducts,
    sortKey,
    facetFilters,
    numericFilters,
    query,
    ruleContexts,
  });
}
